import { useContext, useEffect, useState, useRef } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import logo from "../assets/logo.png";
import logo_icon from "../assets/logo_icon.png";
import {
  MdOutlineArchitecture,
  MdWaterDrop,
  MdEmail
} from "react-icons/md";

import {
  FaBuilding,
  FaCheckCircle,
  FaLinkedinIn,
  FaTwitter,
  FaUserTie
} from "react-icons/fa";

import CustomersHighlight from "./CustomersHighlight";

/* ---------- MOCK TEAM DATA ---------- */
const teamMembers = [
  {
    id: 1,
    name: "James M.",
    role: "Managing Director",
    bio: "Over 15 years of experience in construction supply chain management.",
    image: logo_icon,
  },
  {
    id: 2,
    name: "Sarah K.",
    role: "Operations Lead",
    bio: "Specializes in logistics coordination and quality assurance protocols.",
    image: logo_icon,
  },
  {
    id: 3,
    name: "David O.",
    role: "Technical Consultant",
    bio: "Expert in structural materials and waterproofing system implementation.",
    image: logo_icon,
  },
  {
    id: 4,
    name: "Grace N.",
    role: "Client Relations",
    bio: "Dedicated to ensuring seamless communication and project satisfaction.",
    image: logo_icon,
  },
];

/* ---------- STAT CARD ---------- */
const CleanStat = ({
  target,
  suffix = "",
  duration = 2000,
  label,
  icon: Icon,
  colorClass = "text-primary"
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration, hasAnimated]);

  return (
    <div ref={ref} className="stat-card">
      <div className={`stat-icon-wrapper ${colorClass}`}>
        <Icon size={24} />
      </div>
      <div className={`stat-number ${colorClass}`}>
        {count.toLocaleString()}{suffix}
      </div>
      <div className="stat-label">
        {label}
      </div>
    </div>
  );
};

/* ---------- TEAM CARD ---------- */
const TeamCard = ({ member }) => {
  return (
    <div className="team-card">
      <div className="team-image-wrapper">
        <img src={member.image} alt={member.name} className="team-image" />
        <div className="team-overlay">
          <div className="team-socials d-none">
            <a href="#linkedin" className="social-link"><FaLinkedinIn /></a>
            <a href="#email" className="social-link"><MdEmail /></a>
          </div>
        </div>
      </div>
      <div className="team-content">
        <h4 className="team-name">{member.name}</h4>
        <p className="team-role">{member.role}</p>
        <p className="team-bio">{member.bio}</p>
      </div>
    </div>
  );
};

export default function AboutUsContent() {
  const appContext = useContext(AppContext);
  const { pendingScrollTarget, setPendingScrollTarget } = appContext;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready || !pendingScrollTarget) return;
    const targetId = pendingScrollTarget;
    setTimeout(() => {
      const section = document.getElementById(targetId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "start" });
        setPendingScrollTarget(null);
      }
    }, 220);
  }, [ready, pendingScrollTarget, setPendingScrollTarget]);

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <div className="MainBodySection modern-about-page mb-0">
        <div className="container pt-5">

          {/* HERO */}
          <section className="about-center-hero" id="about-overview">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              About Group V Limited
            </div>
            
            <h2 className="font-bold about-center-title">
              Reliable construction supply and <span className="text-gradient">transport solutions</span> in Tanzania
            </h2>

            <div className="about-center-text-block">
              <p className="about-center-text">
                Group V Limited is based in Dar es Salaam and supports contractors,
                developers, organizations, and homeowners with project-ready materials
                and dependable transportation services.
              </p>
              <p className="about-center-text m-0">
                We help clients reduce project delays and procurement friction through
                tailored solutions, quality-focused sourcing, and practical operational support.
              </p>
            </div>

            <div className="about-center-logo">
              <img src={logo} alt="Group V Limited logo"/>
            </div>
          </section>

          {/* IMPACT STATS */}
          <section className="stats-section-wrapper mt-5" id="impact-stats">
            <div className="about-center-head mb-4 text-center">
              <p className="about-center-section-title text-primary font-bold display-6">
                Impact By The Numbers
              </p>
              <p className="text-muted font-semi-bold subtitle-text">
                Trusted supply tailored for modern construction needs.
              </p>
            </div>

            <div className="row g-4 justify-content-center stat-grid">
              <div className="col-md-6 col-lg-3">
                <CleanStat
                  target={150}
                  suffix="+"
                  label="Projects Supported"
                  icon={FaBuilding}
                />
              </div>
              <div className="col-md-6 col-lg-3">
                <CleanStat
                  target={2500}
                  suffix="+"
                  label="Tons of Steel"
                  icon={MdOutlineArchitecture}
                  colorClass="text-dark"
                />
              </div>
              <div className="col-md-6 col-lg-3">
                <CleanStat
                  target={50000}
                  suffix="+"
                  label="Waterproofed Area"
                  icon={MdWaterDrop}
                  colorClass="text-info"
                />
              </div>
              <div className="col-md-6 col-lg-3">
                <CleanStat
                  target={98}
                  suffix="%"
                  label="Client Satisfaction"
                  icon={FaCheckCircle}
                  colorClass="text-success"
                />
              </div>
            </div>
          </section>

          {/* OUR TEAM SECTION */}
          <section className="team-section mt-5 pt-5" id="our-team">
            <div className="about-center-head mb-5 text-center">
              <h1 className="about-center-section-title text-primary font-bold display-6">
                Our Team
              </h1>
              <p className="text-muted font-semi-bold subtitle-text">
                The experts behind our operational excellence.
              </p>
            </div>

            <div className="row g-4 justify-content-center">
              {teamMembers.map((member) => (
                <div className="col-md-6 col-lg-3" key={member.id}>
                  <TeamCard member={member} />
                </div>
              ))}
            </div>
          </section>

        </div>

        <style>{`
          :root {
            --primary-gradient: linear-gradient(135deg, #0f2f56 0%, #194a84 100%);
            --accent-gradient: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
            --surface-bg: #ffffff;
            --text-muted: #64748b;
          }

          .modern-about-page {
            padding: 64px 0 100px;
            background:
              radial-gradient(circle at 14% 8%, rgba(181, 217, 246, 0.15), transparent 40%),
              linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
            font-family: "Inter", system-ui, -apple-system, sans-serif;
          }

          /* --- HERO STYLES --- */
          .about-center-hero {
            max-width: 900px;
            margin: 0 auto 40px;
            text-align: center;
            border-radius: 32px;
            border: 1px solid rgba(226, 232, 240, 0.6);
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(20px);
            box-shadow: 
              0 20px 40px -10px rgba(15, 47, 86, 0.08),
              0 0 0 1px rgba(255, 255, 255, 0.5);
            padding: 48px 32px;
            position: relative;
            overflow: hidden;
          }

          .about-center-hero::before {
            content: "";
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(56, 189, 248, 0.05) 0%, transparent 60%);
            pointer-events: none;
            z-index: -1;
          }

          .hero-badge {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 16px;
            background: rgba(56, 189, 248, 0.1);
            border: 1px solid rgba(56, 189, 248, 0.2);
            border-radius: 99px;
            font-size: 13px;
            font-weight: 600;
            color: #0369a1;
            margin-bottom: 24px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .badge-dot {
            width: 6px;
            height: 6px;
            background: #0ea5e9;
            border-radius: 50%;
            animation: pulse 2s infinite;
          }

          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(14, 165, 233, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(14, 165, 233, 0); }
            100% { box-shadow: 0 0 0 0 rgba(14, 165, 233, 0); }
          }

          .about-center-title {
            max-width: 820px;
            margin: 0 auto 24px;
            font-size: 32px;
            line-height: 1.3;
            color: #0f172a;
            font-weight: 800;
            letter-spacing: -0.02em;
          }

          .text-gradient {
            background: var(--accent-gradient);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          .about-center-text-block {
            max-width: 700px;
            margin: 0 auto 32px;
          }

          .about-center-text {
            font-size: 17px;
            line-height: 1.7;
            color: #475569;
            margin-bottom: 16px;
          }

          .about-center-logo {
            width: min(90%, 280px);
            margin: 0 auto;
            border: 1px solid rgba(226, 232, 240, 0.8);
            border-radius: 20px;
            background: #ffffff;
            padding: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            transition: transform 0.3s ease;
          }

          .about-center-logo:hover {
            transform: translateY(-4px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          }

          .about-center-logo img {
            width: 100%;
            height: auto;
            object-fit: contain;
            max-height: 80px;
          }

          /* --- STATS STYLES --- */
          .stats-section-wrapper {
            background: #ffffff;
            border-radius: 32px;
            padding: 40px 24px;
            border: 1px solid rgba(226, 232, 240, 0.6);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
          }

          .subtitle-text {
            color: var(--text-muted);
            font-size: 16px;
            margin-top: 8px;
          }

          .row.stat-grid > div {
            display: flex;
          }

          .stat-card {
            flex: 1;
            background: #ffffff;
            border-radius: 20px;
            border: 1px solid #f1f5f9;
            padding: 32px 20px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            gap: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
          }

          .stat-card::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            background: var(--accent-gradient);
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .stat-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            border-color: rgba(56, 189, 248, 0.3);
          }

          .stat-card:hover::before {
            opacity: 1;
          }

          .stat-icon-wrapper {
            width: 56px;
            height: 56px;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(56, 189, 248, 0.1);
            color: #0284c7;
            margin-bottom: 8px;
            transition: all 0.3s ease;
          }

          .stat-card:hover .stat-icon-wrapper {
            background: #0284c7;
            color: #ffffff;
            transform: scale(1.1);
          }

          .stat-number {
            font-size: 36px;
            font-weight: 800;
            line-height: 1;
            letter-spacing: -1px;
            color: #0f172a;
          }

          .stat-label {
            font-size: 13px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #64748b;
            line-height: 1.4;
          }

          /* --- TEAM STYLES --- */
          .team-section {
            position: relative;
          }

          .team-card {
            background: #ffffff;
            border-radius: 24px;
            overflow: hidden;
            border: 1px solid #f1f5f9;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
            transition: all 0.3s ease;
            height: 100%;
            display: flex;
            flex-direction: column;
          }

          .team-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
            border-color: rgba(56, 189, 248, 0.3);
          }

          .team-image-wrapper {
            position: relative;
            height: 280px;
            overflow: hidden;
            display: grid;
            place-items: center;
          }

          .team-image {
            width: 75%;
            height: auto;
            transition: transform 0.5s ease;
          }

          .team-card:hover .team-image {
            transform: scale(1.05);
          }

          .team-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(to top, rgba(15, 47, 86, 0.8), transparent);
            opacity: 0;
            transition: opacity 0.3s ease;
            display: flex;
            align-items: flex-end;
            justify-content: center;
            padding-bottom: 24px;
          }

          .team-card:hover .team-overlay {
            opacity: 1;
          }

          .team-socials {
            display: flex;
            gap: 12px;
            transform: translateY(20px);
            transition: transform 0.3s ease;
          }

          .team-card:hover .team-socials {
            transform: translateY(0);
          }

          .social-link {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.2);
            backdrop-filter: blur(4px);
            color: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            text-decoration: none;
            transition: all 0.2s ease;
            border: 1px solid rgba(255, 255, 255, 0.3);
          }

          .social-link:hover {
            background: #ffffff;
            color: #0f2f56;
            transform: scale(1.1);
          }

          .team-content {
            padding: 24px;
            text-align: center;
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .team-name {
            font-size: 20px;
            font-weight: 700;
            color: #0f172a;
            margin: 0 0 4px;
          }

          .team-role {
            font-size: 14px;
            font-weight: 600;
            color: #0ea5e9;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin: 0 0 12px;
          }

          .team-bio {
            font-size: 14px;
            color: #64748b;
            line-height: 1.6;
            margin: 0;
          }

          /* Responsive Adjustments */
          @media (max-width: 768px) {
            .about-center-title {
              font-size: 26px;
            }
            
            .stat-card {
              padding: 24px 16px;
            }

            .team-image-wrapper {
              height: 240px;
            }
          }
        `}</style>
      </div>

      <CustomersHighlight/>
    </>
  );
}